import type { SearchFilters } from '@content-search/domain';
import { POLICY_FILTER_LABELS } from '@content-search/domain';
import { useStandardLabels } from '@content-search/hooks/useStandardLabels';
import { formatDateTime, getLanguageDisplayName, isDefaultDateRange } from '@content-search/utils';
import { IconButton, CloseSmall, IconButtonSize, IconSize, ButtonType } from '@soteria/design-system-ve';
import { useCallback, useMemo } from 'react';

import * as styles from './SearchPanel.css';

interface SearchFilterBadgesProps {
  searchFilters: SearchFilters;
  isLoading: boolean;
  onRemoveFilter: (filters: Partial<SearchFilters>) => void;
}

// Inline FilterBadge component - simplified since it's only used here
function FilterBadge({ label, onRemove, isLoading = false }: { label: string; onRemove: () => void; isLoading?: boolean }) {
  return (
    <div className={styles.filterBadge}>
      <span>{label}</span>
      <IconButton
        aria-label={`Remove ${label} filter`}
        buttonSize={IconButtonSize.XXSmall}
        buttonType={ButtonType.Tertiary}
        disabled={isLoading}
        icon={CloseSmall}
        iconSize={IconSize.XXSmall}
        style={{ borderRadius: '50%' }}
        title={`Remove ${label} filter`}
        onClick={onRemove}
      />
    </div>
  );
}

export function SearchFilterBadges({ searchFilters, isLoading, onRemoveFilter }: SearchFilterBadgesProps) {
  const { standardLabels } = useStandardLabels();

  // Helper function to get full label name from value/short name
  const getLabelDisplayName = useCallback(
    (labelValue: string) => {
      const standardLabel = standardLabels.find(label => label.value === labelValue || label.name.toLowerCase() === labelValue.toLowerCase());

      return standardLabel ? standardLabel.name : labelValue;
    },
    [standardLabels]
  );

  // Generate filter badges using a comprehensive configuration-driven approach

  const filterBadges = useMemo(() => {
    const badges: React.ReactElement[] = [];
    const { panelFilters } = searchFilters;

    // Comprehensive filter configuration - handles all filter types
    const filterConfigs = [
      // Sampling filter
      {
        condition: () => panelFilters.isSampled !== undefined && panelFilters.isSampled !== true,
        createBadges: () => [
          {
            key: `sampling-${panelFilters.isSampled}`,
            label: panelFilters.isSampled ? 'Sampled' : 'Not Sampled',
            onRemove: () => onRemoveFilter({ panelFilters: { ...panelFilters, isSampled: true } }),
          },
        ],
      },

      // Date range filters
      {
        condition: () => panelFilters.startDate && panelFilters.endDate && !isDefaultDateRange(panelFilters.startDate, panelFilters.endDate),
        createBadges: () => {
          if (!panelFilters.startDate || !panelFilters.endDate) return [];

          return [
            {
              key: 'date-range',
              label: `${formatDateTime(panelFilters.startDate)} - ${formatDateTime(panelFilters.endDate)}`,
              onRemove: () => {
                onRemoveFilter({ panelFilters: { ...panelFilters, startDate: undefined, endDate: undefined } });
              },
            },
          ];
        },
      },
      {
        condition: () => panelFilters.startDate && !panelFilters.endDate,
        createBadges: () => {
          if (!panelFilters.startDate) return [];

          return [
            {
              key: 'start-date',
              label: `After ${formatDateTime(panelFilters.startDate)}`,
              onRemove: () => onRemoveFilter({ panelFilters: { ...panelFilters, startDate: undefined } }),
            },
          ];
        },
      },
      {
        condition: () => panelFilters.endDate && !panelFilters.startDate,
        createBadges: () => {
          if (!panelFilters.endDate) return [];

          return [
            {
              key: 'end-date',
              label: `Before ${formatDateTime(panelFilters.endDate)}`,
              onRemove: () => onRemoveFilter({ panelFilters: { ...panelFilters, endDate: undefined } }),
            },
          ];
        },
      },

      // Language filter
      {
        condition: () => !!panelFilters.languageDetected,
        createBadges: () => {
          if (!panelFilters.languageDetected) return [];

          return [
            {
              key: `language-detected-${panelFilters.languageDetected}`,
              label: getLanguageDisplayName(panelFilters.languageDetected),
              onRemove: () => onRemoveFilter({ panelFilters: { ...panelFilters, languageDetected: undefined } }),
            },
          ];
        },
      },

      // Metadata filters
      {
        condition: () => !!panelFilters.metadata && Object.keys(panelFilters.metadata).length > 0,
        createBadges: () => {
          if (!panelFilters.metadata) return [];

          return Object.entries(panelFilters.metadata).map(([key, value]) => ({
            key: `metadata-${key}-${value}`,
            label: `${key}: ${value}`,
            onRemove: () => {
              const updatedMetadata = { ...panelFilters.metadata };

              delete updatedMetadata[key];
              onRemoveFilter({
                panelFilters: {
                  ...panelFilters,
                  metadata: Object.keys(updatedMetadata).length > 0 ? updatedMetadata : undefined,
                },
              });
            },
          }));
        },
      },

      // Content type filters
      {
        condition: () => !!panelFilters.contentTypes && panelFilters.contentTypes.length > 0,
        createBadges: () => {
          if (!panelFilters.contentTypes) return [];

          return panelFilters.contentTypes.map(contentType => ({
            key: `contentTypes-${contentType}`,
            label: contentType.charAt(0).toUpperCase() + contentType.slice(1),
            onRemove: () =>
              onRemoveFilter({
                panelFilters: { ...panelFilters, contentTypes: panelFilters.contentTypes?.filter(ct => ct !== contentType) || [] },
              }),
          }));
        },
      },

      // Labels filters
      {
        condition: () => !!panelFilters.labels && panelFilters.labels.length > 0,
        createBadges: () => {
          if (!panelFilters.labels) return [];

          return panelFilters.labels.map(label => ({
            key: `labels-${label}`,
            label: getLabelDisplayName(label),
            onRemove: () =>
              onRemoveFilter({
                panelFilters: { ...panelFilters, labels: panelFilters.labels?.filter(l => l !== label) || [] },
              }),
          }));
        },
      },

      // Topics Filters
      {
        condition: () => !!panelFilters.topics && panelFilters.topics.length > 0,
        createBadges: () => {
          if (!panelFilters.topics) return [];

          return panelFilters.topics.map(topic => ({
            key: `topics-${topic}`,
            label: topic,
            onRemove: () =>
              onRemoveFilter({
                panelFilters: { ...panelFilters, topics: panelFilters.topics?.filter(t => t !== topic) || [] },
              }),
          }));
        },
      },

      // Policy filter
      {
        condition: () => !!panelFilters.policyFilter,
        createBadges: () => {
          if (!panelFilters.policyFilter) return [];

          const policyLabel = POLICY_FILTER_LABELS[panelFilters.policyFilter];

          return [
            {
              key: `policy-${panelFilters.policyFilter}`,
              label: policyLabel,
              onRemove: () => onRemoveFilter({ panelFilters: { ...panelFilters, policyFilter: undefined } }),
            },
          ];
        },
      },
    ];

    // Generate badges from configuration
    filterConfigs.forEach(config => {
      if (config.condition()) {
        const badgeConfigs = config.createBadges();

        badgeConfigs.forEach(badgeConfig => {
          badges.push(<FilterBadge key={badgeConfig.key} isLoading={isLoading} label={badgeConfig.label} onRemove={badgeConfig.onRemove} />);
        });
      }
    });

    return badges;
  }, [searchFilters, isLoading, onRemoveFilter, getLabelDisplayName]);

  return filterBadges.length > 0 ? filterBadges : null;
}
